import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    const productHref = productLink?.href || this.dataset.productUrl;

    if (!productHref) return '';

    const url = new URL(productHref, window.location.origin);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    if (this.#buildGamaroCardQuickAddModal()) {
      this.#openQuickAddModal();
      return;
    }

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #getGamaroCardProductData() {
    if (this.dataset.gamaroCardQuickAdd !== 'true') return null;

    const productDataNode = this.querySelector('[data-gamaro-card-product-json]');
    if (!productDataNode?.textContent) return null;

    try {
      return JSON.parse(productDataNode.textContent);
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  #formatMoney(cents) {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'BRL',
    }).format((Number(cents) || 0) / 100);
  }

  #getGamaroInstallmentText(price) {
    if (this.dataset.gamaroInstallmentsEnabled === 'false') return '';

    const count = Math.max(Number(this.dataset.gamaroInstallmentsCount) || 3, 2);
    const mode = this.dataset.gamaroInstallmentsMode || 'without_interest';
    const interestPercent = Number(String(this.dataset.gamaroInstallmentsInterestPercent || '0').replace(',', '.')) || 0;
    let installmentTotal = Number(price) || 0;
    let suffix = this.dataset.gamaroInstallmentsWithoutInterestText || '';

    if (mode === 'with_interest') {
      installmentTotal = installmentTotal * (100 + interestPercent * count) / 100;
      suffix = this.dataset.gamaroInstallmentsWithInterestText || '';
    }

    const installmentValue = installmentTotal / count;
    return `ou ${count}x de ${this.#formatMoney(installmentValue)}${suffix ? ` ${suffix}` : ''}`;
  }

  #buildGamaroCardQuickAddModal() {
    const productData = this.#getGamaroCardProductData();
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!productData || !modalContent) return false;

    const variants = Array.isArray(productData.variants) ? productData.variants : [];
    const optionNames = Array.isArray(productData.options) ? productData.options : [];
    const cardVariantId = this.#getSelectedVariantId();
    const selectedVariant = variants.find((variant) => String(variant.id) === String(cardVariantId))
      || variants.find((variant) => variant.available) || variants[0];
    if (!selectedVariant) return false;

    const wrapper = document.createElement('div');
    wrapper.className = 'gamaro-quick-variant';

    const title = document.createElement('a');
    title.className = 'gamaro-quick-variant__title';
    title.href = this.productPageUrl;
    title.textContent = this.dataset.productTitle || '';
    wrapper.appendChild(title);

    const price = document.createElement('div');
    price.className = 'gamaro-quick-variant__price';
    wrapper.appendChild(price);

    const form = document.createElement('form');
    form.className = 'gamaro-quick-variant__form';
    form.setAttribute('data-type', 'add-to-cart-form');

    const variantIdInput = document.createElement('input');
    variantIdInput.type = 'hidden';
    variantIdInput.name = 'id';
    form.appendChild(variantIdInput);

    const quantityInput = document.createElement('input');
    quantityInput.type = 'hidden';
    quantityInput.name = 'quantity';
    quantityInput.value = '1';
    form.appendChild(quantityInput);

    optionNames.forEach((optionName, optionIndex) => {
      const values = [...new Set(variants.map((variant) => variant.options?.[optionIndex]).filter(Boolean))];
      if (!values.length) return;
      if (values.length === 1) return;

      const normalizedOptionName = String(optionName).trim().toLocaleLowerCase('pt-BR');
      const isColorOption = normalizedOptionName.includes('cor') || normalizedOptionName.includes('color');
      const optionSwatches = productData.optionSwatches?.[optionIndex]?.values || [];

      const fieldset = document.createElement('fieldset');
      fieldset.className = 'gamaro-quick-variant__option';

      const legend = document.createElement('legend');
      const legendLabel = document.createElement('strong');
      legendLabel.textContent = `${optionName}:`;
      const legendValue = document.createElement('span');
      legendValue.dataset.optionValueIndex = String(optionIndex);
      legend.append(legendLabel, document.createTextNode(' '), legendValue);
      fieldset.appendChild(legend);

      const valuesWrap = document.createElement('div');
      valuesWrap.className = 'gamaro-quick-variant__values';

      values.forEach((value) => {
        const label = document.createElement('label');
        label.className = 'gamaro-quick-variant__choice';
        if (isColorOption) {
          label.classList.add(
            'gamaro-quick-variant__choice--color',
            'variant-option__button-label',
            'variant-option__button-label--has-swatch'
          );
          label.title = value;
        }

        const input = document.createElement('input');
        input.type = 'radio';
        input.name = `option-${optionIndex}`;
        input.value = value;
        input.checked = selectedVariant.options?.[optionIndex] === value;

        const text = document.createElement('span');
        if (isColorOption) {
          text.className = 'swatch';
          const normalizedValue = String(value).trim().toLocaleLowerCase('pt-BR');
          const swatch = optionSwatches.find((item) => item?.name === value);
          let background = swatch?.background || '';

          if (!background && (normalizedValue.includes('dour') || normalizedValue.includes('ouro') || normalizedValue.includes('gold'))) {
            background = '#dfa900';
          } else if (!background && (normalizedValue.includes('prat') || normalizedValue.includes('silver'))) {
            background = '#dedede';
          }

          text.style.setProperty('--swatch-background', background || '#eeeeee');
          const accessibleText = document.createElement('span');
          accessibleText.className = 'visually-hidden';
          accessibleText.textContent = value;
          text.appendChild(accessibleText);
        } else {
          text.textContent = value;
        }

        label.append(input, text);
        valuesWrap.appendChild(label);
      });

      fieldset.appendChild(valuesWrap);
      form.appendChild(fieldset);
    });

    const addButton = document.createElement('button');
    addButton.className = 'gamaro-quick-variant__button';
    addButton.type = 'submit';
    form.appendChild(addButton);

    const errorMessage = document.createElement('p');
    errorMessage.className = 'gamaro-quick-variant__error';
    errorMessage.setAttribute('role', 'alert');
    errorMessage.hidden = true;
    form.appendChild(errorMessage);

    const updateSelectedVariant = () => {
      const selectedOptions = optionNames.map((_, optionIndex) => {
        const checked = form.querySelector(`input[name="option-${optionIndex}"]:checked`);
        return checked instanceof HTMLInputElement ? checked.value : selectedVariant.options?.[optionIndex] || '';
      });
      const variant = variants.find((item) => item.options?.every((option, index) => option === selectedOptions[index]));

      if (!variant) {
        variantIdInput.value = '';
        addButton.disabled = true;
        addButton.textContent = this.dataset.gamaroSoldOutLabel || 'Indisponível';
        form.querySelectorAll('[data-option-value-index]').forEach((label) => {
          if (!(label instanceof HTMLElement)) return;
          const optionIndex = Number(label.dataset.optionValueIndex);
          label.textContent = selectedOptions[optionIndex] || '';
        });
        return;
      }

      variantIdInput.value = String(variant.id);
      price.replaceChildren();

      const priceLine = document.createElement('div');
      priceLine.className = 'gamaro-quick-variant__price-line';

      const salePrice = document.createElement('strong');
      salePrice.textContent = this.#formatMoney(variant.price);
      priceLine.appendChild(salePrice);

      if (Number(variant.compare_at_price) > Number(variant.price)) {
        const comparePrice = document.createElement('s');
        comparePrice.textContent = this.#formatMoney(variant.compare_at_price);
        priceLine.appendChild(comparePrice);

        const discount = Math.round(((Number(variant.compare_at_price) - Number(variant.price)) * 100) / Number(variant.compare_at_price));
        if (discount > 0) {
          const discountBadge = document.createElement('span');
          discountBadge.className = 'gamaro-card-discount gamaro-quick-variant__discount';
          discountBadge.textContent = `-${discount}% OFF`;
          priceLine.appendChild(discountBadge);
        }
      }

      price.appendChild(priceLine);

      const installmentText = this.#getGamaroInstallmentText(variant.price);
      if (installmentText) {
        const installment = document.createElement('span');
        installment.className = 'gamaro-quick-variant__installments';
        installment.textContent = installmentText;
        price.appendChild(installment);
      }

      addButton.disabled = !variant.available;
      addButton.textContent = variant.available
        ? this.dataset.gamaroAddLabel || 'Comprar'
        : this.dataset.gamaroSoldOutLabel || 'Esgotado';
      form.querySelectorAll('[data-option-value-index]').forEach((label) => {
        if (!(label instanceof HTMLElement)) return;
        const optionIndex = Number(label.dataset.optionValueIndex);
        label.textContent = variant.options?.[optionIndex] || '';
      });
    };

    form.addEventListener('change', updateSelectedVariant);
    form.addEventListener('submit', async (submitEvent) => {
      submitEvent.preventDefault();
      if (!variantIdInput.value || addButton.disabled || addButton.getAttribute('aria-busy') === 'true') return;

      const defaultButtonText = addButton.textContent;
      errorMessage.hidden = true;
      errorMessage.textContent = '';
      addButton.setAttribute('aria-busy', 'true');
      addButton.disabled = true;
      addButton.textContent = 'Incluindo...';

      const formData = new FormData(form);
      form.querySelectorAll('fieldset').forEach((field) => { field.disabled = true; });
      const sectionIds = [];
      document.querySelectorAll('cart-items-component').forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
      });

      if (sectionIds.length) {
        formData.set('sections', [...new Set(sectionIds)].join(','));
        formData.set('sections_url', window.location.pathname);
      }

      try {
        const response = await fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData,
        });
        const result = await response.json();

        if (!response.ok || result.status) {
          addButton.disabled = false;
          addButton.textContent = defaultButtonText;
          errorMessage.textContent = result.description || result.message || 'Não foi possível adicionar esta peça. Confira a opção escolhida e tente novamente.';
          errorMessage.hidden = false;
          return;
        } else {
          addButton.textContent = 'Pronto!';
          await new Promise((resolve) => setTimeout(resolve, 650));
        }

        await this.#closeQuickAddModal();

        document.dispatchEvent(
          new CustomEvent(ThemeEvents.cartUpdate, {
            bubbles: true,
            detail: {
              resource: result,
              sourceId: 'gamaro-card-quick-add',
              data: {
                didError: Boolean(result.status),
                source: 'product-form-component',
                variantId: variantIdInput.value,
                itemCount: Number(formData.get('quantity')) || 1,
                sections: result.sections,
              },
            },
          })
        );
      } catch (error) {
        addButton.disabled = false;
        addButton.textContent = defaultButtonText;
        errorMessage.textContent = 'Não conseguimos confirmar a inclusão. Confira seu carrinho antes de tentar novamente.';
        errorMessage.hidden = false;
        console.error(error);
      } finally {
        form.querySelectorAll('fieldset').forEach((field) => { field.disabled = false; });
        addButton.removeAttribute('aria-busy');
        updateSelectedVariant();
      }
    });

    wrapper.appendChild(form);
    modalContent.replaceChildren(wrapper);
    updateSelectedVariant();

    return true;
  }

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return Promise.resolve();

    const dialog = dialogComponent.refs?.dialog;
    if (!dialog?.open) return Promise.resolve();

    const closePromise = dialogComponent.closeDialog();
    const fallbackClose = new Promise((resolve) => {
      setTimeout(() => {
        if (dialog.open) {
          const fixedScrollY = Math.abs(parseInt(document.body.style.top || '0', 10));
          const scrollY = fixedScrollY || window.scrollY || window.pageYOffset || 0;

          dialog.close();
          dialog.classList.remove('dialog-closing');
          document.body.style.width = '';
          document.body.style.position = '';
          document.body.style.top = '';
          window.scrollTo({ top: scrollY, behavior: 'instant' });
        }

        resolve();
      }, 350);
    });

    return Promise.race([closePromise, fallbackClose]);
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
